import * as Yup from "yup";
import { Formik, FormikProps } from "formik";
import { FormTextInput } from "../../components/FormFields";
import { FC, useEffect, useState } from "react";
import { useAppInventory, useAppSelector } from "../../store/hooks";
import {
  setInventoryCleaning,
  setInventoryForm,
  setInventoryHeader,
} from "../../store/slices/inventory";
import { InventoryHeaderInterface, StatusInventory } from "../../interfaces/InventoryInterface";
import { PrimaryButton } from "../../components/Buttons";
import { alertService, loaderService } from "../../services";
import classNames from "classnames";
import { createInventoryHeader, updateInventoryHeader } from "../../services/inventoryServices";

export interface InventoryManifestFormValues {
  observation: string;
}

interface FormProps {
  editMode: boolean;
  setEditMode: (b: boolean) => void;
  formik: FormikProps<InventoryManifestFormValues>;
  inventoryHeader: InventoryHeaderInterface | null;
}
const Form: FC<FormProps> = ({ editMode, setEditMode, formik, inventoryHeader }) => {
  const inventory = useAppInventory();
  const loading = loaderService.useIsLoading();
  const cleaning = useAppSelector((state) => state.inventory.cleaning);
  const user = useAppSelector((state) => state.user);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    inventory(setInventoryForm(formik.values));
  }, [formik.values, inventory]);

  useEffect(() => {
    if (cleaning) {
      formik.setValues({
        ...formik.values,
        observation: "",
      });
      formik.setTouched({
        observation: false,
      });
      inventory(setInventoryCleaning(false));
      setEditMode(true);
    }
  }, [cleaning, inventory, formik, setEditMode]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="flex flex-col bg-white gap-8 rounded-lg border px-8 pb-6 pt-4"
    >
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          {editMode && (
            <FormTextInput
              id="observation"
              name="observation"
              label="Observación"
              value={formik.values.observation}
              onChange={formik.handleChange}
              placeholder="Introduzca la observación"
              maxLength={150}
            />
          )}

          {!editMode && (
            <>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex flex-col sm:flex-row flex-1 gap-4">
                  {inventoryHeader?.inventoryHeaderID && (
                  <div className="flex-1">
                      <>
                        <p className="font-medium leading-6 text-gray-900">
                          Código del inventario
                        </p>
                        <p className="truncate mt-1 text-sm">
                          {inventoryHeader.inventoryHeaderCode}
                        </p>
                      </>
                  </div>
                  )}
                  <div className="flex-1">
                      <>
                        <p className="font-medium leading-6 text-gray-900">
                          Observación
                        </p>
                        <p className="truncate mt-1 text-sm">
                          {formik.values.observation ? formik.values.observation : "N/D"}
                        </p>
                      </>
                  </div>
                  <div className="flex-1">
                      <>
                        <p className="font-medium leading-6 text-gray-900">
                          Fecha de creación
                        </p>
                        <p className="truncate mt-1 text-sm">
                          {inventoryHeader?.inventoryHeaderID ? (inventoryHeader.inventoryOpenDate ? new Date(inventoryHeader.inventoryOpenDate).toLocaleString() : '') : currentTime.toLocaleString()}
                        </p>
                      </>
                  </div>
                  <div className="flex-1">
                      <>
                        <p className="font-medium leading-6 text-gray-900">
                          Usuario de creación
                        </p>
                        <p className="truncate mt-1 text-sm">
                          {inventoryHeader?.inventoryHeaderID ? inventoryHeader.creationUser : user.user?.userLogin}
                        </p>
                      </>
                  </div>
                </div>
              </div>
              
              {inventoryHeader?.statusID === StatusInventory.CLOSED && (
                <>
                  <p className="font-medium leading-6 text-gray-900">Fecha de apertura</p>
                  <p className="mt-1 text-sm">{inventoryHeader.inventoryOpenDate ? new Date(inventoryHeader.inventoryOpenDate).toLocaleString() : ''}</p>
                  <p className="font-medium leading-6 text-gray-900">Fecha de cierre</p>
                  <p className="mt-1 text-sm">{inventoryHeader.inventoryClosedDate ? new Date(inventoryHeader.inventoryClosedDate).toLocaleString() : ''}</p>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex w-full justify-end mt-4">
        <PrimaryButton
          className={classNames(!editMode && "hidden")}
          disabled={loading}
        >
          {!inventoryHeader?.inventoryHeaderID ? "Generar Inventario" : "Guardar cabecera"}
        </PrimaryButton>

        <PrimaryButton
          type="button"
          className={classNames(editMode && "hidden")}
          onClick={() => setEditMode(true)}
        >
          Editar cabecera
        </PrimaryButton>
      </div>
    </form>
  );
};

const InventoryForm: FC = () => {
  const inventory = useAppInventory();
  const user = useAppSelector((state) => state.user);
  const inventoryHeader = useAppSelector(
    (state) => state.inventory.inventoryHeader
  );
  const inventoryForm = useAppSelector((state) => state.inventory.inventoryForm);

  const [editMode, setEditMode] = useState(true);

  const validationSchema = Yup.object().shape({
    // No validation needed
  });

  const handleSubmit = async (values: InventoryManifestFormValues) => {
    const userLogin = user.user?.userLogin;
    const buSource = user.businessUnit;

    if (!userLogin || !buSource) {
      return;
    }

    if (!inventoryHeader) {
      const inventorysHeader: InventoryHeaderInterface = {
              buCodeSource: buSource.code,
              creationUser: userLogin,
              observation: values.observation,
              inventoryTypeID: 1
            };
            const newInventoryHeader = await createInventoryHeader(inventorysHeader);
      
            if (
              newInventoryHeader.didError ||
              !newInventoryHeader.model ||
              !newInventoryHeader.model.inventoryHeaderID
            ) {
              alertService.error(
                "Hubo un error creando el inventario",
                newInventoryHeader.errorMessage
              );
              return;
            }
      
            inventory(setInventoryHeader(newInventoryHeader.model));
            setEditMode(false);       

    }else{
      const inventorysHeader: InventoryHeaderInterface = {
              inventoryHeaderID: inventoryHeader.inventoryHeaderID!,
              observation: values.observation,
              updateUser: userLogin,
            };
            const updatedInventoryHeader = await updateInventoryHeader(inventorysHeader);
      
            if (updatedInventoryHeader.didError || !updatedInventoryHeader.model) {
              alertService.error(
                "Hubo un error actualizando el inventario",
                updatedInventoryHeader.errorMessage
              );
              return;
            }
      
            inventory(
              setInventoryHeader({
                ...inventoryHeader,
                observation: values.observation
              })
            );
            setEditMode(false);
    }
  };

  useEffect(() => {
    setEditMode(!inventoryHeader);
  }, [inventoryHeader]);

  return (
    <Formik<InventoryManifestFormValues>
      initialValues={inventoryForm}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        loaderService.start();
        await handleSubmit(values);
        loaderService.stop();
      }}
    >
      {(formik) => (
        <Form editMode={editMode} setEditMode={setEditMode} formik={formik} inventoryHeader={inventoryHeader} />
      )}
    </Formik>
  );
};

export default InventoryForm;
